import { getCore as get, postCore as post } from "../../utils/httpInterceptor";

/**
 * Custom (hand-written) service for the "Seleccion de bobinas" (CMT_INF)
 * screen replica. Backend contract: CmtSeleccionBobinasController
 * (@RequestMapping("/api/legacy/cmt-seleccionbobinas")).
 *
 * Notes on the real backend shapes (confirmed by reading the backend source,
 * not just the functional spec):
 * - search-candidates returns CmtSeleccionBobinasCandidate records with
 *   fields: tipo (enum name string "DISPONIBLE_FISICO"/"PENDIENTE_RECIBIR"),
 *   itemId, sameItem, inventBatchId, inventTransId, inventLocationId,
 *   wmsLocationId, qtyAvailable, expectedDate, antiqueDate, margenPercent,
 *   mermaPercent, costoTransporte, conflict (enum name string), and
 *   conflictSalesLineRecId. It does NOT include the current
 *   selcompleta/selresto/selparcial/delegacion flags for the line - those
 *   live in separate CmtSeleccionBobinas rows, fetched via listBySalesLine()
 *   (the generated generic CRUD list, filtered by saleslinerecid) and matched
 *   client-side by itemId + batch/trans id.
 * - toggle-selection body/response use plain lower-case field names
 *   (saleslinerecid, tipo, bobinaitemid, bobinainventbatchid,
 *   bobinainventtransid, selcompleta, selresto, selparcial) mirroring
 *   CmtSeleccionBobinasDTO.
 */

const BASE_PATH = "/api/legacy/cmt-seleccionbobinas";

const toQueryString = (params) => {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, String(value));
    }
  });
  return query.toString();
};

class cmtSeleccionBobinasService {
  /** GET /search-candidates?salesLineRecId= -> CmtSeleccionBobinasCandidate[] */
  static async searchCandidates(salesLineRecId) {
    if (!salesLineRecId) return [];
    const qs = toQueryString({ salesLineRecId });
    return get(`${BASE_PATH}/search-candidates?${qs}`);
  }

  /**
   * GET the generic CRUD list filtered by saleslinerecid, to know the
   * already-persisted selection rows (selcompleta/selresto/selparcial,
   * delegacion1-3, pctdelegacion1-3, qtydelegacion1-3, recId) for a line.
   */
  static async listBySalesLine(salesLineRecId) {
    if (!salesLineRecId) return [];
    const qs = toQueryString({ saleslinerecid: salesLineRecId, size: 200, page: 0 });
    const page = await get(`${BASE_PATH}?${qs}`);
    return Array.isArray(page?.content) ? page.content : Array.isArray(page) ? page : [];
  }

  /** POST /toggle-selection -> CmtSeleccionBobinasDTO */
  static async toggleSelection(payload) {
    return post(`${BASE_PATH}/toggle-selection`, payload);
  }

  /** POST /{recId}/set-delegacion -> CmtSeleccionBobinasDTO */
  static async setDelegacion(recId, payload) {
    return post(`${BASE_PATH}/${encodeURIComponent(recId)}/set-delegacion`, payload);
  }

  /** GET /check-coverage?iSalesId= -> CmtSeleccionBobinasCoverageLine[] (only lines out of tolerance) */
  static async checkCoverage(iSalesId) {
    if (!iSalesId) return [];
    const qs = toQueryString({ iSalesId });
    return get(`${BASE_PATH}/check-coverage?${qs}`);
  }

  /** POST /send-to-management?iSalesId= -> { iSalesId, cmtSeleccionBobinasEstadoPV, cmtSeleccionBobinasEnviadoPor } */
  static async sendToManagement(iSalesId) {
    const qs = toQueryString({ iSalesId });
    return post(`${BASE_PATH}/send-to-management?${qs}`);
  }

  /** POST /mark-reviewed?iSalesId= (requires sales.seleccionbobinas.approve) */
  static async markReviewed(iSalesId) {
    const qs = toQueryString({ iSalesId });
    return post(`${BASE_PATH}/mark-reviewed?${qs}`);
  }

  /** GET /can-create-mfo?iSalesId= -> { canCreateMfo } */
  static async canCreateMfo(iSalesId) {
    if (!iSalesId) return { canCreateMfo: false };
    const qs = toQueryString({ iSalesId });
    return get(`${BASE_PATH}/can-create-mfo?${qs}`);
  }

  /** GET /permissions -> { canApprove } */
  static async getPermissions() {
    return get(`${BASE_PATH}/permissions`);
  }
}

export default cmtSeleccionBobinasService;
